/**
 * E-shops List Page
 * Lists all data sources with "eshop" module enabled
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../services/api';
import { ShoppingCart, Database, RefreshCw, AlertCircle, CheckCircle, Store, BarChart3 } from 'lucide-react';

interface EshopDataSource {
  _id: string;
  id: string;
  name: string;
  type: string;
  purpose?: string;
  isActive: boolean;
  config: {
    database?: string;
    host?: string;
    port?: number;
    uri?: string;
  };
  metadata?: {
    totalCustomers?: number;
    totalOrders?: number;
    totalAbandonedCarts?: number;
  };
}

const EshopsPage: React.FC = () => {
  const [eshops, setEshops] = useState<EshopDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuthStore();
  const { getSelection } = useDataSourceStore();
  const navigate = useNavigate();

  // Get the selected company from the data source store
  const selection = getSelection();
  const companyId = selection?.companyId;

  useEffect(() => {
    fetchEshops();
  }, [companyId, JSON.stringify(selection?.dataSourceIds)]);

  const fetchEshops = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!companyId) {
        setError('No company selected');
        setLoading(false);
        return;
      }

      // Fetch company data sources with eshop module
      const response = await api.get(`/api/v1/companies/${companyId}`);
      const data = response.data;

      // Filter data sources that have "eshop" module enabled
      // IMPORTANT: Only show actual e-commerce databases (PostgreSQL, MySQL, MongoDB)
      // Exclude API integrations like GetResponse, EmailOctopus which are for email marketing
      let eshopDataSources = (data.data.dataSources || []).filter((ds: any) => {
        const hasEshopModule = ds.availableModules?.includes('eshop');
        const isEcommerceDatabase = ['postgresql', 'mysql', 'mongodb'].includes(ds.type);
        return hasEshopModule && isEcommerceDatabase;
      });

      // If user has selected specific data sources, filter to only show those
      if (selection?.dataSourceIds && selection.dataSourceIds.length > 0) {
        eshopDataSources = eshopDataSources.filter((ds: any) =>
          selection.dataSourceIds.includes(ds.id || ds._id)
        );
      }

      setEshops(eshopDataSources);
    } catch (err: any) {
      console.error('Error fetching e-shops:', err);
      setError(err.message || 'Failed to load e-shops');
    } finally {
      setLoading(false);
    }
  };

  const handleEshopClick = (eshop: EshopDataSource) => {
    navigate(`/eshops/${eshop.id || eshop._id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading E-shops
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
          <button
            onClick={fetchEshops}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            E-shops
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your integrated e-commerce databases
          </p>
        </div>
        <button
          onClick={fetchEshops}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* E-shops Grid */}
      {eshops.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No E-shops Configured
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Add a data source with "E-shop Integration" module to get started
          </p>
          <button
            onClick={() => navigate('/admin/companies')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Companies
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {eshops.map(eshop => (
            <div
              key={eshop.id || eshop._id}
              onClick={() => handleEshopClick(eshop)}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl hover:border-indigo-500 dark:hover:border-indigo-500 transition-all cursor-pointer group"
            >
              {/* Icon and Status */}
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800 transition-colors">
                  <Database className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                {eshop.isActive ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {/* Name and Details */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {eshop.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {eshop.purpose || 'E-commerce database'}
              </p>

              {/* Database Info */}
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Type:</span>
                  <span className="uppercase">{eshop.type}</span>
                </div>
                {eshop.config?.database && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Database:</span>
                    <span>{eshop.config.database}</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              {eshop.metadata && (
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-center">
                    <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                      {eshop.metadata.totalCustomers || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Customers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600 dark:text-green-400">
                      {eshop.metadata.totalOrders || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Orders</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      {eshop.metadata.totalAbandonedCarts || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Abandoned</div>
                  </div>
                </div>
              )}

              {/* Overview Button */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/eshops/${eshop.id || eshop._id}/data-overview`);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors text-sm font-medium"
                >
                  <BarChart3 className="w-4 h-4" />
                  Data Overview
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EshopsPage;
